import React from 'react';
import { Link } from '@tanstack/react-router';
import { Building2, ChevronRight, ExternalLink, MapPin } from 'lucide-react';
import { ProfileCompany } from 'app/javascript/types';
import { Avatar, AvatarFallback, AvatarImage } from 'app/javascript/components/ui/avatar';
import { Card } from 'app/javascript/components/ui/card';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from 'app/javascript/components/ui/empty';
import { ROUTES } from 'app/javascript/constants/routes';

interface CompanySectionProps {
  companies: ProfileCompany[];
}

const CompanySection: React.FC<CompanySectionProps> = ({ companies }) => {
  if (!companies || companies.length === 0) {
    return (
      <Empty className="border bg-card/40">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Building2 />
          </EmptyMedia>
          <EmptyTitle>Sem academia vinculada</EmptyTitle>
          <EmptyDescription>Não está associado a nenhuma equipe.</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  const locationText = (company: ProfileCompany) => {
    const parts = [company.city, company.state].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  return (
    <div className="flex flex-col gap-3">
      {companies.map((company) => {
        const location = locationText(company);

        return (
          <Card key={company.id} className="overflow-hidden">
            <Link
              to={ROUTES.COMPANY_DETAILS}
              params={{ slug: company.slug }}
              className="group flex items-center gap-4 p-4 transition-colors hover:bg-secondary/40"
            >
              <Avatar className="size-12 rounded-lg border border-border">
                {company.logoUrl ? <AvatarImage src={company.logoUrl} alt={company.name} className="object-cover" /> : null}
                <AvatarFallback className="rounded-lg bg-secondary">
                  <Building2 className="size-6 text-muted-foreground" />
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold text-foreground transition-colors group-hover:text-yellow-400">
                  {company.name}
                </h3>
                <div className="mt-0.5 flex flex-wrap gap-x-4 gap-y-0.5">
                  {location && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <MapPin className="size-3 shrink-0 text-muted-foreground/70" />
                      <span className="truncate">{location}</span>
                    </span>
                  )}
                  {company.websiteUrl && (
                    <span className="flex items-center gap-1 text-xs text-yellow-400/80">
                      <ExternalLink className="size-3 shrink-0" />
                      <span className="truncate">{company.websiteUrl}</span>
                    </span>
                  )}
                </div>
              </div>
              <ChevronRight className="size-5 shrink-0 text-muted-foreground transition-colors group-hover:text-yellow-400" />
            </Link>
          </Card>
        );
      })}
    </div>
  );
};

export default CompanySection;
